import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize, Pause, Play, Settings as SettingsIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AdminPage } from './components/admin/AdminPage';
import { ArticleModal, useArticle } from './components/ArticleModal';
import { DayEventsModal, useDayEvents } from './components/DayEventsModal';
import { PlannerTaskModal } from './components/PlannerTaskModal';
import { usePlannerTask } from './store/plannerTask';
import { HouseAdSlide } from './components/HouseAdSlide';
import { Layout } from './components/Layout';
import { PrivacyPolicy, TermsOfService } from './components/LegalPage';
import { MediaStage } from './components/MediaStage';
import { JoinPage } from './components/JoinPage';
import { PairPage } from './components/PairPage';
import { SettingsPanel } from './components/settings/SettingsPanel';
import { useAdManager } from './hooks/useAdManager';
import { toggleFullscreen, useActivity, useWakeLock } from './hooks/useDisplay';
import { useDisplayScale } from './hooks/useDisplayScale';
import { useEffectiveLayout } from './hooks/useLayout';
import { useMediaQueue } from './hooks/useMediaQueue';
import { useScreenHeartbeat } from './hooks/useScreenHeartbeat';
import { useRotation, useScheduledOff } from './hooks/useScreenPlan';
import { useSettingsSync } from './hooks/useSettingsSync';
import { fontById, loadFont } from './lib/fonts';
import { sanitizeCss } from './lib/sanitizeCss';
import { endpoints } from './services/api';
import { useLayoutEditor } from './store/layoutEditor';
import { useNowShowing } from './store/nowShowing';
import { useScreen } from './store/screen';
import { isLicensed, useSession } from './store/session';
import { onProfileChangedElsewhere } from './store/profile';
import { restoreLocalMedia, useSettings } from './store/settings';
import { StagingGate } from './components/StagingGate';

export function App() {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/privacy') return <PrivacyPolicy />;
  if (path === '/terms') return <TermsOfService />;
  if (path === '/pair') return <PairPage />;
  if (path === '/join') return <JoinPage />;
  if (path === '/admin') return <AdminPage />;
  return (
    <StagingGate>
      <Display />
    </StagingGate>
  );
}

const AUTH_ERRORS: Record<string, string> = {
  cancelled: 'Microsoft sign-in was cancelled.',
  email_exists:
    'An account with that email already exists. Sign in with your password, then connect Microsoft from Settings → Account.',
  linked_elsewhere: 'That Microsoft account is already connected to a different KCL Canvas account.',
  sign_in_first: 'Sign in first, then connect your Microsoft account.',
  session_changed: 'You signed out while connecting. Please sign in and try again.',
  not_configured: 'Microsoft sign-in isn’t switched on yet.',
  restricted: 'That account isn’t invited to this test site.',
};

function useThemeAndCss(): void {
  const theme = useSettings((s) => s.theme);
  const customCss = useSettings((s) => s.customCss);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  const safe = useMemo(() => sanitizeCss(customCss), [customCss]);
  useEffect(() => {
    let el = document.getElementById('kcl-custom-css') as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = 'kcl-custom-css';
      document.head.appendChild(el);
    }
    el.textContent = safe; // textContent, never innerHTML
  }, [safe]);
}

/** Settings → Appearance → Fonts: load the chosen fonts and point the CSS at them. */
function useFonts(): void {
  const { body, heading } = useSettings((s) => s.fonts);
  useEffect(() => {
    void loadFont(body);
    if (heading !== 'same') void loadFont(heading);
    const root = document.documentElement.style;
    root.setProperty('--font-body', fontById(body).family);
    root.setProperty('--font-heading', fontById(heading === 'same' ? body : heading).family);
  }, [body, heading]);
}

function Display() {
  useThemeAndCss();
  useFonts();
  useDisplayScale();
  useSettingsSync();
  useScreenHeartbeat();

  const { load, loaded, user, setUser } = useSession();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const editing = useLayoutEditor((s) => s.editing);
  // Switched off by this screen's schedule (Settings → Screens): black, stopped, allowed to
  // sleep – but never while someone has its settings or the layout editor open.
  const { off, awakeUntil } = useScheduledOff(settingsOpen || editing);
  useWakeLock(!off);
  const [settingsTab, setSettingsTab] = useState<string | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const active = useActivity();

  useEffect(() => {
    void load();
    void restoreLocalMedia();
  }, [load]);

  // Another tab signed in or out: reload so this one shows (and saves to) the same account.
  useEffect(() => onProfileChangedElsewhere(() => window.location.reload()), []);

  // Returning from Stripe Checkout or a Microsoft sign-in/connect redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const authError = params.get('auth_error');
    const signedIn = params.get('signed_in');
    const connected = params.get('connected');
    if (!checkout && !authError && !signedIn && !connected) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (authError === 'linked_elsewhere' || authError === 'only_sign_in') {
      // Explained in Settings → Account, with the option to move the account here.
      useSession.getState().setLinkProblem({ code: authError, email: params.get('ms') ?? '' });
      setSettingsTab('account');
      setSettingsOpen(true);
      return;
    }
    if (authError) {
      setToast(AUTH_ERRORS[authError] ?? 'Microsoft sign-in didn’t complete. Please try again.');
      return;
    }
    if (connected === 'powerbi') {
      setToast('Power BI connected – choose reports and dashboards in Settings → Widgets → Power BI.');
      void useSession.getState().refreshMicrosoft();
      return;
    }
    if (signedIn || connected) {
      setToast(signedIn ? 'Signed in with Microsoft.' : 'Microsoft account connected – choose OneDrive folders in Settings → Media → Cloud storage.');
      return; // load() above already picked up the new session
    }
    if (checkout === 'success') {
      setToast('Thank you! Your lifetime licence is active – no more ads.');
      // The webhook can land a moment after the redirect.
      const refresh = () => endpoints.me().then((r) => r.user && setUser(r.user)).catch(() => undefined);
      void refresh();
      window.setTimeout(refresh, 4000);
    } else if (checkout === 'link_expired') {
      setToast('That payment link has expired – scan the new code on your TV.');
    } else if (checkout === 'already_licensed') {
      setToast('This account already has a lifetime licence – no need to pay again.');
    } else if (checkout === 'unavailable') {
      setToast('Payments are unavailable right now – please try again later.');
    } else {
      setToast('Checkout cancelled – nothing was charged.');
    }
  }, [setUser]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Licence bought on another device (pay-on-your-phone): thank the viewer on this screen too.
  const licensed = isLicensed(user);
  const wasLicensed = useRef<boolean | null>(null);
  useEffect(() => {
    if (wasLicensed.current === false && licensed) setToast('Thank you! Your lifetime licence is active – no more ads.');
    if (loaded) wasLicensed.current = licensed;
  }, [licensed, loaded]);

  const adsEnabled = loaded && !licensed && !off;
  const { ad, dismiss } = useAdManager(adsEnabled);
  const queue = useMediaQueue(paused || settingsOpen || ad !== null || editing || off);
  useRotation(settingsOpen || editing);

  // "Arrange widgets on screen" closes settings so the screen itself can be edited.
  useEffect(() => {
    if (editing) setSettingsOpen(false);
  }, [editing]);

  // Tell the photo location & date widget what's on screen.
  const showNow = useNowShowing((s) => s.show);
  const currentItem = queue.current?.type === 'media' ? queue.current.item : null;
  useEffect(() => showNow(currentItem, queue.cycle, queue.intervalSec), [currentItem, queue.cycle, queue.intervalSec, showNow]);

  const openSettings = useCallback((tab?: string) => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  }, []);

  // The licence section offers pay-on-your-phone (TVs can't use Stripe's page with a remote).
  const removeAds = useCallback(() => {
    dismiss();
    openSettings('account');
  }, [dismiss, openSettings]);

  // Keyboard / TV remote shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        settingsOpen ||
        useScreen.getState().off ||
        useLayoutEditor.getState().editing ||
        useArticle.getState().article ||
        useDayEvents.getState().day ||
        usePlannerTask.getState().open ||
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;
      switch (e.key) {
        case 'ArrowRight':
        case 'MediaTrackNext':
          queue.next();
          break;
        case 'ArrowLeft':
        case 'MediaTrackPrevious':
          queue.prev();
          break;
        case ' ':
        case 'MediaPlayPause':
          e.preventDefault();
          setPaused((p) => !p);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'Enter':
          // Remote OK button: open settings when nothing on screen has focus.
          if (document.activeElement && document.activeElement !== document.body) return;
          e.preventDefault();
          openSettings();
          break;
        case 's':
        case 'S':
        case 'm':
        case 'M':
        case 'ContextMenu':
          openSettings();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settingsOpen, queue, openSettings]);

  // Command Center stacks widgets top-right, so the controls move left there.
  const layout = useEffectiveLayout();
  const controlsSide = layout === 'command' ? 'left-4' : 'right-4';
  const hasMedia = useSettings((s) => s.media.items.length > 0 || s.media.cloudFolders.length > 0);
  const hasLocation = useSettings((s) => s.weather.location !== null);
  const photosOnly = useSettings((s) => s.photosOnly);
  const firstRun = !hasMedia && !hasLocation;

  // Off: nothing but black. Widgets, the slideshow and ads are unmounted, so their timers,
  // videos and network requests stop too; check-ins carry on so changes still arrive.
  if (off) return <main className="hide-cursor h-full w-full bg-black" aria-label="Screen off by its schedule" />;

  return (
    <main className={`relative h-full w-full overflow-hidden ${active || settingsOpen ? '' : 'hide-cursor'}`}>
      {/* Portrait Stack shows the photo in the top part of the screen only. */}
      <div className={layout === 'portrait-stack' && !photosOnly ? 'absolute inset-x-0 top-0 h-[42%]' : 'absolute inset-0'}>
      <MediaStage
        entry={queue.current}
        cycle={queue.cycle}
        intervalSec={queue.intervalSec}
        onEnded={queue.next}
        onError={queue.markFailed}
        onSize={queue.reportSize}
        loopVideo={queue.total <= 1}
      />
      </div>
      <Layout />

      <AnimatePresence>{ad && <HouseAdSlide key={ad.id} ad={ad} onClose={dismiss} onRemoveAds={() => void removeAds()} />}</AnimatePresence>

      {/* Controls fade in on activity. */}
      <AnimatePresence>
        {(active || paused) && !settingsOpen && !editing && (
          <motion.div
            className={`glass absolute ${controlsSide} top-4 z-20 flex items-center gap-1 p-1.5`}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {queue.total > 1 && (
              <>
                <IconButton label="Previous" onClick={queue.prev} icon={<ChevronLeft className="h-5 w-5" />} />
                <IconButton
                  label={paused ? 'Play' : 'Pause'}
                  onClick={() => setPaused((p) => !p)}
                  icon={paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                />
                <IconButton label="Next" onClick={queue.next} icon={<ChevronRight className="h-5 w-5" />} />
              </>
            )}
            <IconButton label="Full screen (F)" onClick={toggleFullscreen} icon={<Maximize className="h-5 w-5" />} />
            <IconButton label="Settings (S)" onClick={() => openSettings()} icon={<SettingsIcon className="h-5 w-5" />} />
          </motion.div>
        )}
      </AnimatePresence>

      {firstRun && !settingsOpen && !editing && (
        <button
          onClick={() => openSettings('media')}
          className="glass absolute left-1/2 top-8 z-20 -translate-x-1/2 px-5 py-3 text-sm"
        >
          Welcome to KCL Canvas – press <kbd className="rounded border border-[var(--border)] px-1.5">OK</kbd> or tap here to add
          your photos, weather and calendar.
        </button>
      )}

      <AnimatePresence>
        {toast && (
          <motion.div
            className="glass absolute bottom-24 left-1/2 z-50 -translate-x-1/2 px-5 py-3 text-sm"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            role="status"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {awakeUntil && (active || settingsOpen) && (
        <div className="glass absolute bottom-24 left-4 z-30 px-4 py-2 text-xs" role="status">
          This screen is scheduled off – it stays on until{' '}
          {new Date(awakeUntil).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} while it’s being used.
        </div>
      )}

      <ArticleModal />
      <DayEventsModal />
      <PlannerTaskModal />
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={settingsTab} />
    </main>
  );
}

function IconButton({ label, onClick, icon }: { label: string; onClick: () => void; icon: ReactNode }) {
  return (
    <button onClick={onClick} aria-label={label} title={label} className="rounded-xl p-2.5 transition hover:bg-[var(--border)]">
      {icon}
    </button>
  );
}
